import { Injectable, computed, signal } from '@angular/core';

/** Where the top bar's back action goes: a URL, or router commands. */
export type TopbarBackTarget = string | readonly unknown[];

/**
 * What a page puts in the top bar's left slot (canon §3b):
 * - `{ back }` — an entity page (NBO-2532): the section label becomes `‹ Products & Clusters` and
 *   leads back to the listing;
 * - `{ labelKey }` — a singleton page (NBO-2545, Settings): its own label, plain, no chevron.
 *   Settings is reached from the user menu, not the rail, so the rail-derived label would be empty.
 */
export type TopbarClaim = { readonly back: TopbarBackTarget } | { readonly labelKey: string };

/**
 * The top bar's left slot, claimed by the page on screen. The page CLAIMS it for as long as it
 * lives and the returned function releases it — a page that is destroyed takes its chevron or its
 * label with it, so a screen never has to clear it by hand. No route data: the label must not wait
 * for the guards on a cold load (the NBO-2343 reasoning for chrome visibility).
 *
 * The latest claim wins, whatever its kind; releasing it falls back to the one before (an entity
 * page opened over another, e.g. during a route transition). With no claim the top bar shows the
 * rail's section label.
 */
@Injectable({ providedIn: 'root' })
export class TopbarClaimService {
  private readonly claims = signal<readonly { claim: TopbarClaim }[]>([]);

  private readonly latest = computed<TopbarClaim | null>(() => this.claims().at(-1)?.claim ?? null);

  /** The latest claim's back action, when it is a back claim. */
  readonly backTarget = computed<TopbarBackTarget | null>(() => {
    const claim = this.latest();
    return claim && 'back' in claim ? claim.back : null;
  });

  /** The latest claim's page label (an i18n key), when it is a label claim. */
  readonly labelKey = computed<string | null>(() => {
    const claim = this.latest();
    return claim && 'labelKey' in claim ? claim.labelKey : null;
  });

  claim(claim: TopbarClaim): () => void {
    const entry = { claim };
    this.claims.update((claims) => [...claims, entry]);
    return () => this.claims.update((claims) => claims.filter((c) => c !== entry));
  }
}
